/**
 * GltfMissingFilesView
 *
 * Full-page view shown when a .gltf file is missing external resource files.
 * Lets the user provide the missing files so the upload can proceed.
 */

import { useState } from 'react';
import { ArrowBackIcon, ExclamationIcon, SuccessIcon, UploadIcon } from '../../../icons';
import { Dropzone } from './Dropzone';

export interface GltfMissingFilesViewProps {
  /** Name of the .gltf file that needs resources */
  gltfFileName: string;
  /** List of file names that are still missing */
  missingFiles: string[];
  /** Called with the files the user selected/dropped */
  onSubmit: (files: File[]) => void;
  /** Called when the user cancels */
  onClose: () => void;
}

const basename = (uri: string) => uri.split('/').pop() ?? uri;

export const GltfMissingFilesView = ({
  gltfFileName,
  missingFiles,
  onSubmit,
  onClose,
}: GltfMissingFilesViewProps) => {
  const [addedFiles, setAddedFiles] = useState<File[]>([]);

  const resolvedNames = new Set(addedFiles.map((f) => f.name));
  // Match by basename so "textures/foo.png" is resolved by dropping "foo.png"
  const stillMissing = missingFiles.filter((uri) => !resolvedNames.has(basename(uri)));
  const allResolved = stillMissing.length === 0;

  const expectedBasenames = new Set(missingFiles.map(basename));

  const handleDrop = (fileList: FileList) => {
    const incoming = Array.from(fileList).filter((f) => expectedBasenames.has(f.name));
    setAddedFiles((prev) => {
      const existing = new Map(prev.map((f) => [f.name, f]));
      for (const f of incoming) existing.set(f.name, f);
      return Array.from(existing.values());
    });
  };

  const handleSubmit = () => {
    if (allResolved) onSubmit(addedFiles);
  };

  return (
    <div className="fullscreen-view gltf-missing-files-view">
      <header className="fullscreen-view__header">
        <button
          className="btn btn-ghost p0"
          onClick={onClose}
          aria-label="Go back"
        >
          <ArrowBackIcon />
          Upload missing files : {gltfFileName}
        </button>
      </header>

      <div className="fullscreen-view__body">
        <p className="fullscreen-view__subtitle">
          Locate the missing files and upload them to continue (max size: 20 MB)
        </p>

        <div className="gltf-missing-files-view__list-section">
          <ul className="gltf-missing-files-view__file-list">
            {stillMissing.map((name) => (
              <li key={name} className="gltf-missing-files-view__file-item gltf-missing-files-view__missing-item">
                <ExclamationIcon />
                {name}
              </li>
            ))}
          </ul>
          {addedFiles.length > 0 && (
            <ul className="gltf-missing-files-view__file-list">
              {addedFiles.map((file) => (
                <li key={file.name} className="gltf-missing-files-view__file-item">
                  <SuccessIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)" />
                  <span>
                    {file.name}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <Dropzone onUpload={handleDrop}>
          <div className="gltf-missing-files-view__dropzone">
            <label className="btn btn-small btn-link">
              <UploadIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)" />
              <input
                type="file"
                multiple
                onChange={(e) => e.target.files && handleDrop(e.target.files)}
                style={{ display: 'none' }}
              />
            </label>
            <p>Browse files, or drop here to upload</p>

          </div>
        </Dropzone>


        <div className="gltf-missing-files-view__actions">
          <button className="btn btn-small btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-small btn-primary"
            onClick={handleSubmit}
            disabled={!allResolved}
          >
            Upload
          </button>
        </div>
      </div>
    </div>
  );
};
